import React, { useState } from "react";
import {
  Modal,
  Card,
  Grid,
  Stack,
  Skeleton,
  Typography,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import ProductComboBox from "pages/FlexProducts/components/ProductComboBox";
import toast from "react-hot-toast";
import ReactSelect from "react-select";
import noPreviewImage from "assets/images/no-preview.png";
import useConfiguratorRuleEngine from "hooks/useConfiguratorRuleEngine";
import { ProductConfiguratorApiService } from "api/productConfigurator.api";
import Styles from "pages/components/common/Styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PropTypes from "prop-types";
const containerStyes = {
  width: { xs: "96%", md: "85%", lg: "60%" },
  maxHeight: "92vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 3,
};

const resolveTemplateId = (product) => {
  if (!product) return null;
  return (
    product.configTemplateId ||
    product.templateId ||
    product.productConfiguratorTemplateId ||
    product.productTemplateId ||
    null
  );
};

const extractTemplateId = (payload) => {
  if (!payload) return null;

  const entry = Array.isArray(payload) ? payload[0] : payload;
  if (!entry) return null;

  return (
    entry.id ||
    entry.templateId ||
    entry.configTemplateId ||
    entry.productConfiguratorTemplateId ||
    entry.productTemplateId ||
    entry.template?.id ||
    null
  );
};

const AddQuotationModal = ({ open, handleClose, onAddItems }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isResolvingTemplate, setIsResolvingTemplate] = useState(false);
  const thumbnailRef = React.useRef(null);

  const {
    loading,
    optionGroups,
    options,
    selectedOptions,
    visibleOptions,
    disabledOptions,
    price,
    selectOption,
    productDetails,
  } = useConfiguratorRuleEngine(selectedTemplateId);

  const handleProductSelect = async (product) => {
    setSelectedProduct(product || null);
    setSelectedImageIndex(0);

    if (!product) {
      setSelectedTemplateId(null);
      return;
    }

    const productId = product?.id || product?.productId;
    const directTemplateId = resolveTemplateId(product);
    if (directTemplateId) {
      setSelectedTemplateId(directTemplateId);
      return;
    }

    if (!productId) {
      setSelectedTemplateId(null);
      return;
    }

    try {
      setIsResolvingTemplate(true);

      const templateRes = await ProductConfiguratorApiService.getTemplateByProduct(productId);
      const byProductPayload = templateRes?.data?.data ?? templateRes?.data ?? null;
      const byProductTemplateId = extractTemplateId(byProductPayload);

      if (byProductTemplateId) {
        setSelectedTemplateId(byProductTemplateId);
      } else {
        setSelectedTemplateId(null);
      }
    } catch (error) {
      console.warn("No template found for this product ID, proceeding normally.");
      setSelectedTemplateId(null);
    } finally {
      setIsResolvingTemplate(false);
    }
  };

  const getOptionLabel = (opt) => {
    const adjustment = Number(opt.priceAdjustment || 0);
    if (adjustment === 0) return opt.name;
    if (opt.priceAdjustmentType === "Percentage") return `${opt.name} (+${adjustment}%)`;
    return `${opt.name} (+$${adjustment})`;
  };

  const product = productDetails || selectedProduct;

  const productImages = product?.imageFiles?.length
    ? product.imageFiles.map((img) => img.fileUrl)
    : [noPreviewImage];

  const productTitle = product ? `${product?.sku} - ${product?.title}` : "Select a product";
  const productDescription =
    product?.shortDescription || "Choose a product to view Product details.";
  const isOptionVisible = (optionId) => visibleOptions.size === 0 || visibleOptions.has(optionId);

  const handleThumbnailClick = (index) => setSelectedImageIndex(index);

  const scrollThumbnails = (direction) => {
    if (!thumbnailRef.current) return;
    thumbnailRef.current.scrollBy({
      left: direction === "left" ? -90 : 90,
      behavior: "smooth",
    });
  };

  const resetModal = () => {
    setSelectedProduct(null);
    setSelectedTemplateId(null);
    setSelectedImageIndex(0);
  };

  const handleAddToQuotation = () => {
    if (!product) {
      toast.error("Please select and configure a product");
      return;
    }

    let configurationDetails = "";
    if (selectedTemplateId && Object.keys(selectedOptions).length > 0) {
      const details = [];
      optionGroups
        .filter((group) => (options[group.id] || []).some((opt) => isOptionVisible(opt.id)))
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .forEach((group) => {
          const selectedValue = selectedOptions[group.id];
          if (selectedValue) {
            const groupOptions = (options[group.id] || []).filter((opt) => isOptionVisible(opt.id));
            const selectedOption = groupOptions.find((opt) => opt.id === selectedValue);
            if (selectedOption) {
              details.push(`${group.name} - ${selectedOption.name}`);
            }
          }
        });
      if (details.length > 0) {
        configurationDetails = ` (${details.join(", ")})`;
      }
    }

    const mappedItems = [
      {
        description: product?.shortDescription || product?.description || product?.title || "",
        itemNo: product?.sku || String(product?.id || ""),
        name: `${product?.sku || "PRD"} - ${
          product?.title || "Configured Product"
        }${configurationDetails}`,
        quantity: "1",
        price: String(Number(price?.total || 0)),
        selectedItem: {
          id: product?.id,
          title: product?.title,
        },
        selectedProduct: {
          id: `${product?.id}-${Date.now()}`,
          productId: product?.id,
          title: product?.title,
          sku: product?.sku,
          quantity: 1,
          price: Number(price?.total || 0),
          description: product?.shortDescription || product?.description || "",
        },
        thumbnail: productImages[0],
        configuration: selectedTemplateId ? selectedOptions : {},
        templateId: selectedTemplateId,
      },
    ];

    onAddItems(mappedItems);
    toast.success("Quote item added");
    handleClose();
    resetModal();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose();
        resetModal();
      }}
      aria-labelledby="add-quotation-modal"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <SoftBox sx={containerStyes}>
        <Card sx={{ p: 2, boxShadow: "none" }}>
          <SoftTypography variant="h4" mb={2}>
            Add Quotation Item
          </SoftTypography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SoftTypography variant="h6">Select Product</SoftTypography>
              <ProductComboBox onSelect={handleProductSelect} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {(loading && selectedTemplateId) || isResolvingTemplate ? (
            <Skeleton variant="rectangular" height={400} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Box display="flex" flexDirection="column" gap={1.5} mt={1.5}>
                  <Box
                    component="img"
                    src={productImages[selectedImageIndex]}
                    alt="Product preview"
                    sx={{
                      width: "100%",
                      maxWidth: "280px",
                      height: "260px",
                      objectFit: "cover",
                      borderRadius: "12px",
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      border: "1px solid #f0f0f0",
                    }}
                  />

                  <Box display="flex" alignItems="center" gap={0.5} sx={{ maxWidth: "280px" }}>
                    {productImages.length > 3 && (
                      <IconButton
                        size="small"
                        onClick={() => scrollThumbnails("left")}
                        sx={{
                          minWidth: 28,
                          width: 28,
                          height: 28,
                          bgcolor: "#f5f5f5",
                          "&:hover": { bgcolor: "#e0e0e0" },
                        }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}

                    <Box
                      ref={thumbnailRef}
                      display="flex"
                      gap={1}
                      sx={{
                        overflowX: "auto",
                        scrollBehavior: "smooth",
                        flex: 1,
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                      }}
                    >
                      {productImages.map((img, index) => (
                        <Box
                          key={`${img}-${index}`}
                          component="img"
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          onClick={() => handleThumbnailClick(index)}
                          sx={{
                            width: "70px",
                            height: "70px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            cursor: "pointer",
                            flexShrink: 0,
                            border:
                              selectedImageIndex === index
                                ? "2px solid #17c1e8"
                                : "2px solid transparent",
                            opacity: selectedImageIndex === index ? 1 : 0.6,
                          }}
                        />
                      ))}
                    </Box>

                    {productImages.length > 3 && (
                      <IconButton
                        size="small"
                        onClick={() => scrollThumbnails("right")}
                        sx={{
                          minWidth: 28,
                          width: 28,
                          height: 28,
                          bgcolor: "#f5f5f5",
                          "&:hover": { bgcolor: "#e0e0e0" },
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={7}>
                <Box mt={2}>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {productTitle}
                  </SoftTypography>
                  <SoftTypography variant="body2" fontSize="12px" color="text">
                    {productDescription}
                  </SoftTypography>
                </Box>

                <Stack spacing={2} mt={1.5}>
                  {optionGroups
                    .filter((group) =>
                      (options[group.id] || []).some((opt) => isOptionVisible(opt.id))
                    )
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((group) => {
                      const groupOptions = (options[group.id] || []).filter((opt) =>
                        isOptionVisible(opt.id)
                      );
                      const selectedValue = selectedOptions[group.id] || "";

                      const selectOptions = groupOptions
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((opt) => ({
                          value: opt.id,
                          label: getOptionLabel(opt),
                          isDisabled: disabledOptions.has(opt.id),
                        }));

                      const selectedOption = selectOptions.find(
                        (opt) => opt.value === selectedValue
                      );

                      return (
                        <Box key={group.id}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            {group.name}
                          </SoftTypography>

                          <ReactSelect
                            placeholder={`Select ${group.name}`}
                            value={selectedOption}
                            options={selectOptions}
                            onChange={(option) => selectOption(group.id, option?.value)}
                            styles={Styles.multiSelectStyles}
                            menuPortalTarget={document.body}
                          />
                        </Box>
                      );
                    })}

                  {!selectedTemplateId && selectedProduct && !isResolvingTemplate && (
                    <SoftTypography variant="caption" color="warning" display="block">
                      No configurator template found for this product. You can still add and edit
                      the quote item.
                    </SoftTypography>
                  )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box mt={2} p={2} bgcolor="#fafafa" borderRadius="8px">
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                      <Typography
                        variant="body2"
                        sx={{ textDecoration: "line-through", color: "text.secondary" }}
                      >
                        List Price: ${((price?.total || 0) * 1.15).toFixed(2)}
                      </Typography>
                      <Typography variant="h3" fontWeight="bold" color="text.primary">
                        ${(price?.total || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}

          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <SoftTypography variant="h6">
              Current Price: ${(price?.total || 0).toFixed(2)}
            </SoftTypography>
            <Stack direction="row" spacing={1}>
              <SoftButton
                variant="outlined"
                onClick={() => {
                  handleClose();
                  resetModal();
                }}
              >
                Cancel
              </SoftButton>
              <SoftButton
                color="success"
                variant="contained"
                onClick={handleAddToQuotation}
                disabled={!product}
              >
                Add to Quotation Items
              </SoftButton>
            </Stack>
          </SoftBox>
        </Card>
      </SoftBox>
    </Modal>
  );
};

AddQuotationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onAddItems: PropTypes.func.isRequired,
};

export default AddQuotationModal;
